import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "app/store";
import {
  GetNotifications,
  MarkAllNotificationsRead,
  MarkNotificationRead,
} from "api/services/notifications";
import {
  clearNotifications,
  appendNotifications,
  markAllNotificationsRead as markAllNotificationsReadAction,
  setNotificationUnreadCount,
  setNotifications,
  updateNotificationRead,
  upsertNotification,
} from "reducers/notificationReducer";
import { getUserRoom, SocketEvents } from "config/socket";
import { subscribeToSocketEvent, syncSocketRoom } from "utils/socketClient";
import { NotificationItemType } from "types/loadedData";
import { resolveDashboardNotificationTarget } from "utils/notificationNavigation";
import { playNotificationSound } from "utils/notificationSound";

interface UseNotificationsOptions {
  subscribe?: boolean;
}

export default function useNotifications({
  subscribe = true,
}: UseNotificationsOptions = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);
  const { notifications, unreadCount, loaded } = useSelector(
    (state: RootState) => state.notification
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const loadNotifications = useCallback(
    async ({ limit = 30, offset = 0, append = false } = {}) => {
      if (!user?._id) return null;

      append ? setIsLoadingMore(true) : setIsLoading(true);
      setError("");
      try {
        const feedResponse = await GetNotifications({
          limit,
          offset,
          setLoading: undefined,
        });
        dispatch(
          append
            ? appendNotifications(feedResponse.notifications || [])
            : setNotifications(feedResponse.notifications || [])
        );
        dispatch(
          setNotificationUnreadCount(Number(feedResponse.unreadCount || 0))
        );
        setHasMore(Boolean(feedResponse.hasMore));
        setTotalCount(Number(feedResponse.count || 0));
        return feedResponse;
      } catch (loadError) {
        setError("load");
        throw loadError;
      } finally {
        append ? setIsLoadingMore(false) : setIsLoading(false);
      }
    },
    [dispatch, user?._id]
  );

  const refreshNotifications = useCallback(
    async (limit = 30) => {
      try {
        return await loadNotifications({ limit, offset: 0, append: false });
      } catch {
        return null;
      }
    },
    [loadNotifications]
  );

  const loadMoreNotifications = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;
    try {
      await loadNotifications({
        limit: 30,
        offset: notifications.length,
        append: true,
      });
    } catch {
      // The page renders the hook error state.
    }
  }, [hasMore, isLoadingMore, loadNotifications, notifications.length]);

  useEffect(() => {
    if (!user?._id) {
      dispatch(clearNotifications());
      return;
    }

    let mounted = true;

    const load = async () => {
      try {
        await loadNotifications({ limit: 30, offset: 0, append: false });
      } catch (error) {
        if (mounted) {
          console.warn("Failed to load dashboard notifications:", error);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [dispatch, loadNotifications, user?._id]);

  useEffect(() => {
    if (!subscribe || !user?._id) {
      return;
    }

    const unsubscribeRoom = syncSocketRoom(getUserRoom(user._id));
    const unsubscribeNotificationNew = subscribeToSocketEvent<NotificationItemType>(
      SocketEvents.NotificationNew,
      (notification) => {
        if (!notification.readAt) {
          playNotificationSound();
        }
        dispatch(upsertNotification(notification));
      }
    );
    const unsubscribeNotificationRead = subscribeToSocketEvent<NotificationItemType>(
      SocketEvents.NotificationRead,
      (notification) => {
        dispatch(
          updateNotificationRead({
            notificationId: notification._id,
            readAt: notification.readAt || new Date().toISOString(),
          })
        );
      }
    );
    const unsubscribeNotificationSync = subscribeToSocketEvent<{
      unreadCount?: number;
      notification?: NotificationItemType | null;
    }>(SocketEvents.NotificationSync, (payload) => {
      if (typeof payload.unreadCount === "number") {
        dispatch(setNotificationUnreadCount(payload.unreadCount));
      }

      if (payload.notification) {
        dispatch(upsertNotification(payload.notification));
      }
    });

    return () => {
      unsubscribeRoom();
      unsubscribeNotificationNew();
      unsubscribeNotificationRead();
      unsubscribeNotificationSync();
    };
  }, [dispatch, subscribe, user?._id]);

  const markNotificationReadById = useCallback(
    async (notification: NotificationItemType) => {
      if (!notification?._id) return notification;

      const response = await MarkNotificationRead({
        id: notification._id,
        setLoading: undefined,
      });

      if (response.notification) {
        dispatch(
          updateNotificationRead({
            notificationId: response.notification._id,
            readAt: response.notification.readAt || new Date().toISOString(),
          })
        );
      }

      if (typeof response.unreadCount === "number") {
        dispatch(setNotificationUnreadCount(response.unreadCount));
      }

      return response.notification || notification;
    },
    [dispatch]
  );

  const markAllAsRead = useCallback(async () => {
    const response = await MarkAllNotificationsRead({ setLoading: undefined });
    dispatch(markAllNotificationsReadAction());

    if (typeof response.unreadCount === "number") {
      dispatch(setNotificationUnreadCount(response.unreadCount));
    }

    return response.unreadCount ?? 0;
  }, [dispatch]);

  const openNotificationTarget = useCallback(
    (notification: NotificationItemType) => {
      const target = resolveDashboardNotificationTarget(notification);

      if (!target) {
        return;
      }

      navigate(target.path, {
        state: target.state,
      });
    },
    [navigate]
  );

  return {
    notifications,
    unreadCount,
    loaded,
    refreshNotifications,
    loadMoreNotifications,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    totalCount,
    markNotificationReadById,
    markAllAsRead,
    openNotificationTarget,
  };
}
